import React, { useEffect, useState } from 'react'
import { classNames } from '../../lib/utils/classNames'
import { useTranslation } from 'next-i18next'

type CountdownProps = {
  targetDate: string
  className?: string
  textClassname?: string
  numberClassname?: string
  onCountdownComplete: (onCountdownComplete: boolean) => void
}

const secondsInMillisecond = 1000
const minutesInMillisecond = secondsInMillisecond * 60
const hoursInMillisecond = minutesInMillisecond * 60
const daysInMillisecond = hoursInMillisecond * 24

export default function Countdown({
  targetDate,
  className,
  onCountdownComplete,
  numberClassname = 'text-xl font-bold sm:text-5xl md:text-4xl xl:text-6xl',
  textClassname = 'text-xs xl:text-xl',
}: CountdownProps) {
  const { t } = useTranslation('originals/global')

  const [timeRemaining, setTimeRemaining] = useState<number>(0)

  const days = Math.floor(timeRemaining / daysInMillisecond)
  const hours = Math.floor(
    (timeRemaining % daysInMillisecond) / hoursInMillisecond,
  )
  const minutes = Math.floor(
    (timeRemaining % hoursInMillisecond) / minutesInMillisecond,
  )
  const seconds = Math.floor(
    (timeRemaining % minutesInMillisecond) / secondsInMillisecond,
  )

  useEffect(() => {
    const targetDateTime = new Date(targetDate).getTime()

    const countdownTimer = setInterval(() => {
      const now = new Date().getTime()
      const remaining = targetDateTime - now

      if (remaining < 0) {
        onCountdownComplete(true)
        clearInterval(countdownTimer)
      }

      setTimeRemaining(remaining)
    }, 1000)

    return () => {
      clearInterval(countdownTimer)
    }
  }, [targetDate])

  useEffect(() => {
    setTimeRemaining(new Date(targetDate).getTime() - new Date().getTime())
  }, [])

  return (
    <div className={classNames('relative flex', className ? className : '')}>
      <div className="mx-auto grid grid-cols-4 gap-6 text-center md:gap-10">
        <div>
          <div className={numberClassname}>{days}</div>
          <div className={textClassname}>{t('countdown.days')}</div>
        </div>
        <div>
          <div className={numberClassname}>{hours}</div>
          <div className={textClassname}>{t('countdown.hours')}</div>
        </div>
        <div>
          <div className={numberClassname}>{minutes}</div>
          <div className={textClassname}>{t('countdown.minutes')}</div>
        </div>
        <div>
          <div className={numberClassname}>{seconds}</div>
          <div className={textClassname}>{t('countdown.seconds')}</div>
        </div>
      </div>
    </div>
  )
}
